import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../lib/LanguageContext';
import { X, Home, Film, Tv, Heart, Bookmark, Clock, Shield, LogOut, FileText, Calendar } from '../common/Icons';
import AdBanner from '../common/AdBanner';

const GENRES = [
  { name: 'Action', slug: 'action' },
  { name: 'Comedy', slug: 'comedy' },
  { name: 'Drama', slug: 'drama' },
  { name: 'Horror', slug: 'horror' },
  { name: 'Sci-Fi', slug: 'sci-fi' },
  { name: 'Thriller', slug: 'thriller' },
  { name: 'Romance', slug: 'romance' },
  { name: 'Animation', slug: 'animation' },
  { name: 'Fantasy', slug: 'fantasy' },
  { name: 'Documentary', slug: 'documentary' },
];

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}

export default function Sidebar({ open, onClose }: SidebarProps) {
  const { user, logout } = useAuth();
  const { t } = useLanguage();

  const handleLinkClick = () => onClose();

  return (
    <>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 lg:hidden" onClick={onClose} />
      )}

      <aside
        className={`fixed top-0 left-0 bottom-0 w-72 bg-surface-950 border-r border-surface-800 z-50 transform transition-transform duration-300 lg:hidden ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-4 h-16 border-b border-surface-800">
          <span className="text-lg font-bold">
            <span className="text-primary">Cine</span>Verse
          </span>
          <button onClick={onClose} className="p-2 -mr-2 text-surface-400 hover:text-white">
            <X />
          </button>
        </div>

        <div className="overflow-y-auto h-[calc(100vh-64px)] p-4 scrollbar-thin">
          <div className="mb-6">
            <Link
              to="/"
              onClick={handleLinkClick}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
            >
              <Home size={18} />
              <span className="text-sm font-medium">{t('home')}</span>
            </Link>
            <Link
              to="/movies/popular"
              onClick={handleLinkClick}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
            >
              <Film size={18} />
              <span className="text-sm font-medium">{t('movies')}</span>
            </Link>
            <Link
              to="/tvshows/popular"
              onClick={handleLinkClick}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
            >
              <Tv size={18} />
              <span className="text-sm font-medium">{t('tvShows')}</span>
            </Link>
            <Link
              to="/articles"
              onClick={handleLinkClick}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
            >
              <FileText size={18} />
              <span className="text-sm font-medium">{t('articles')}</span>
            </Link>
            <Link
              to="/calendar"
              onClick={handleLinkClick}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
            >
              <Calendar size={18} />
              <span className="text-sm font-medium">{t('releaseCalendarFooter')}</span>
            </Link>
          </div>

          <div className="mb-6">
            <h3 className="px-3 text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">
              Movie Genres
            </h3>
            {GENRES.map((genre) => (
              <Link
                key={genre.slug}
                to={`/genre/movie/${genre.slug}`}
                onClick={handleLinkClick}
                className="block px-3 py-2 rounded-lg text-sm text-surface-400 hover:text-white hover:bg-surface-800 transition-colors"
              >
                {genre.name}
              </Link>
            ))}
          </div>

          <div className="mb-6">
            <h3 className="px-3 text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">
              TV Genres
            </h3>
            {GENRES.map((genre) => (
              <Link
                key={genre.slug}
                to={`/genre/tv/${genre.slug}`}
                onClick={handleLinkClick}
                className="block px-3 py-2 rounded-lg text-sm text-surface-400 hover:text-white hover:bg-surface-800 transition-colors"
              >
                {genre.name}
              </Link>
            ))}
          </div>

          <div className="mb-6 flex justify-center">
            <AdBanner placement="sidebar" />
          </div>

          {user && (
            <div className="mb-6">
              <h3 className="px-3 text-xs font-semibold text-surface-500 uppercase tracking-wider mb-2">
                My Account
              </h3>
              <Link
                to="/favorites"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
              >
                <Heart size={18} />
                <span className="text-sm font-medium">{t('favorites')}</span>
              </Link>
              <Link
                to="/watchlist"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
              >
                <Bookmark size={18} />
                <span className="text-sm font-medium">{t('watchlist')}</span>
              </Link>
              <Link
                to="/history"
                onClick={handleLinkClick}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
              >
                <Clock size={18} />
                <span className="text-sm font-medium">{t('watchHistory')}</span>
              </Link>
              {user.role === 'admin' && (
                <Link
                  to="/admin"
                  onClick={handleLinkClick}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-surface-300 hover:text-white hover:bg-surface-800 transition-colors"
                >
                  <Shield size={18} />
                  <span className="text-sm font-medium">Admin</span>
                </Link>
              )}
              <button
                onClick={() => {
                  logout();
                  onClose();
                }}
                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-red-400 hover:text-red-300 hover:bg-surface-800 transition-colors"
              >
                <LogOut size={18} />
                <span className="text-sm font-medium">{t('signOut')}</span>
              </button>
            </div>
          )}

          {!user && (
            <div className="px-3">
              <Link
                to="/login"
                onClick={handleLinkClick}
                className="block w-full text-center px-4 py-2.5 bg-primary hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {t('signIn')}
              </Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
